import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import LinkIcon from '@mui/icons-material/Link';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { InsertDriveFile, PictureAsPdf } from '@mui/icons-material';
import { Box, CircularProgress, Dialog, IconButton, Stack, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { ScrollMode, SpecialZoomLevel, Viewer } from '@react-pdf-viewer/core';
import { scrollModePlugin } from '@react-pdf-viewer/scroll-mode';
import moment from 'moment';
import React, { Fragment, memo, useCallback, useEffect, useMemo, useState } from "react";
import awsConfig from '../../../../../aws-exports';
import { s3Download, s3DownloadExternalBucket } from '../../../../../helpers/s3';
import { CompanyFile, Dashboard, EmailBody } from '../../../../../types/files';
import { FileIconAtom } from '../../../../atoms/dashboard-details-v2/FileAttachment';
import XlsIcon from "../../../../../assets/images/excel.png";
import PptIcon from "../../../../../assets/images/powerpoint.png";
import WordIcon from "../../../../../assets/images/word.png";
import ZipIcon from "../../../../../assets/images/zip.png";
import GoogleDocsIcon from "../../../../../assets/images/google-docs.png";
import GoogleSheetsIcon from "../../../../../assets/images/google-sheets.png";
import GoogleSlidesIcon from "../../../../../assets/images/google-slides.png";
import DocSendIcon from "../../../../../assets/images/docsend.png";
import { FolderChip } from '../../../../atoms/folder-label/FolderChip';

// Link type enumeration
type LinkType =
    | 'google-docs'
    | 'google-sheets'
    | 'google-slides'
    | 'google-forms'
    | 'google-drive'
    | 'office365-word'
    | 'office365-excel'
    | 'office365-powerpoint'
    | 'sharepoint'
    | 'docsend'
    | null;

export interface Attachment extends CompanyFile {
    isLink: boolean;
    filePath?: string;
    fileType?: string;
    linkUrl?: string;
    linkType?: LinkType;
};

// Helper function to detect Google Drive link type
export const detectGoogleLinkType = (url: string): LinkType => {
    try {
        const urlObj = new URL(url);
        const hostname = urlObj.hostname.toLowerCase();
        const pathname = urlObj.pathname.toLowerCase();

        if (!hostname.includes('google.com')) {
            return null;
        }

        // Check pathname first for specific document types (more specific)
        if (pathname.includes('/spreadsheet')) {
            return 'google-sheets';
        }
        if (pathname.includes('/presentation')) {
            return 'google-slides';
        }
        if (pathname.includes('/document')) {
            return 'google-docs';
        }
        if (pathname.includes('/form')) {
            return 'google-forms';
        }

        // Then check specific subdomains (less specific but still useful)
        if (hostname.includes('sheets.google.com')) {
            return 'google-sheets';
        }
        if (hostname.includes('slides.google.com')) {
            return 'google-slides';
        }
        if (hostname.includes('docs.google.com') && pathname.includes('/document')) {
            return 'google-docs';
        }
        if (hostname.includes('forms.google.com')) {
            return 'google-forms';
        }
        if (hostname.includes('drive.google.com')) {
            return 'google-drive';
        }

        return null;
    } catch {
        return null;
    }
};

// Helper function to detect Microsoft Office 365 link type
export const detectOfficeLinkType = (url: string): LinkType => {
    try {
        const urlObj = new URL(url);
        const hostname = urlObj.hostname.toLowerCase();
        const pathname = urlObj.pathname.toLowerCase();

        if (!hostname.includes('office.com') &&
            !hostname.includes('sharepoint.com') &&
            !hostname.includes('live.com') &&
            !hostname.includes('onedrive.') &&
            !hostname.includes('1drv.ms')) {
            return null;
        }

        // Check for Word documents
        if (pathname.includes('/word/') ||
            pathname.includes('.docx') ||
            pathname.includes('.doc') ||
            hostname.includes('word.office.com')) {
            return 'office365-word';
        }

        // Check for Excel spreadsheets
        if (pathname.includes('/excel/') ||
            pathname.includes('.xlsx') ||
            pathname.includes('.xls') ||
            hostname.includes('excel.office.com')) {
            return 'office365-excel';
        }

        // Check for PowerPoint presentations
        if (pathname.includes('/powerpoint/') ||
            pathname.includes('.pptx') ||
            pathname.includes('.ppt') ||
            hostname.includes('powerpoint.office.com')) {
            return 'office365-powerpoint';
        }

        // Generic SharePoint/OneDrive
        if (hostname.includes('sharepoint.com') ||
            hostname.includes('onedrive.') ||
            hostname.includes('1drv.ms')) {
            return 'sharepoint';
        }

        return null;
    } catch {
        return null;
    }
};

// Helper function to detect DocSend link type
export const detectDocSendLinkType = (url: string): LinkType => {
    try {
        const urlObj = new URL(url);
        const hostname = urlObj.hostname.toLowerCase();

        if (hostname.includes('docsend.com')) {
            return 'docsend';
        }

        return null;
    } catch {
        return null;
    }
};

// Helper function to get friendly name for link type
const getLinkTypeName = (linkType: LinkType): string => {
    switch (linkType) {
        case 'google-docs':
            return 'Google Docs';
        case 'google-sheets':
            return 'Google Sheets';
        case 'google-slides':
            return 'Google Slides';
        case 'google-forms':
            return 'Google Forms';
        case 'google-drive':
            return 'Google Drive';
        case 'office365-word':
            return 'Microsoft Word Online';
        case 'office365-excel':
            return 'Microsoft Excel Online';
        case 'office365-powerpoint':
            return 'Microsoft PowerPoint Online';
        case 'sharepoint':
            return 'SharePoint/OneDrive';
        case 'docsend':
            return 'DocSend';
        default:
            return 'External Service';
    }
};

const useModalStyles = makeStyles({
    dialog: {
        '& .MuiDialog-paper': {
            width: '95%',
            maxWidth: '1400px',
            height: '90vh',
            margin: '20px',
            borderRadius: '16px',
            overflow: 'hidden',
        },
    },
});

const useHeaderStyles = makeStyles({
    root: {
        padding: '20px 24px',
        borderBottom: '1px solid #e5e7eb',
        backgroundColor: '#f9fafb',
    },
    title: {
        fontSize: '18px',
        fontWeight: 600,
        color: '#1a1a1a',
    },
    closeButton: {
        width: '36px',
        height: '36px',
        borderRadius: '8px',
        backgroundColor: '#f3f4f6',
        color: '#6b7280',
        '&:hover': {
            backgroundColor: '#e5e7eb',
            color: '#1a1a1a',
        },
    },
});

const useTimelineStyles = makeStyles({
    sidebar: {
        width: '360px',
        borderRight: '1px solid #e5e7eb',
        backgroundColor: '#f9fafb',
        overflowY: 'auto',
        flexShrink: 0,
    },
    header: {
        padding: '20px 20px 16px 20px',
        borderBottom: '2px solid #e5e7eb',
        backgroundColor: 'white',
    },
    headerTitle: {
        fontSize: '11px',
        fontWeight: 600,
        color: '#9ca3af',
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        marginBottom: '8px',
    },
    count: {
        fontSize: '24px',
        color: '#1a1a1a',
        fontWeight: 700,
    },
    list: {
        padding: '16px',
    },
    icon: {
        height: 18,
        width: 18,
    },
    dateGroup: {
        marginBottom: '24px',
        position: 'relative',
        '&::before': {
            content: '""',
            position: 'absolute',
            left: '9px',
            top: '8px',
            bottom: '-24px',
            width: '2px',
            backgroundColor: '#d1d5db',
        },
        '&:last-child::before': {
            display: 'none',
        },
    },
    dateHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '6px 0 6px 32px',
        marginBottom: '12px',
        borderBottom: '1px solid #e5e7eb',
        position: 'relative',
        '&::before': {
            content: '""',
            position: 'absolute',
            left: '4px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: '12px',
            height: '12px',
            borderRadius: '50%',
            backgroundColor: '#14b8a6',
            boxShadow: '0 0 0 3px rgba(20, 184, 166, 0.1)',
        },
    },
    dateText: {
        fontFamily: 'Inter',
        fontSize: '12px',
        fontWeight: 600,
        color: '#6b7280',
        textTransform: 'uppercase',
    },
    dateRelative: {
        fontFamily: 'Inter',
        fontSize: '12px',
        color: '#9ca3af',
        fontWeight: 400,
        marginLeft: '8px',
    },
    dateBadge: {
        backgroundColor: '#14b8a6',
        color: 'white',
        padding: '4px 12px',
        borderRadius: '6px',
        fontSize: '12px',
        fontWeight: 600,
    },
    timelineItem: {
        display: 'flex',
        gap: '12px',
        padding: 0,
        marginBottom: '12px',
        marginLeft: '32px',
        cursor: 'pointer',
        transition: 'all 0.2s',
    },
    timelineContent: {
        flex: 1,
        minWidth: 0,
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '12px',
        border: '2px solid transparent',
        transition: 'all 0.2s',
        '&:hover': {
            backgroundColor: '#f3f4f6',
        },
    },
    timelineContentSelected: {
        borderColor: '#14b8a6',
        backgroundColor: '#f0fdfa',
    },
    fileName: {
        fontSize: '14px',
        fontWeight: 500,
        color: '#1a1a1a',
        lineHeight: 1.4,
        wordBreak: 'break-word',
    },
    fileSource: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '2px 8px',
        backgroundColor: '#eff6ff',
        border: '1px solid #bfdbfe',
        borderRadius: '4px',
        fontSize: '11px',
        color: '#1e40af',
        fontWeight: 500,
        marginTop: '4px',
        width: 'fit-content',
    },
    fileSourceLink: {
        color: '#1e40af',
        textDecoration: 'none',
        fontWeight: 500,
        '&:hover': {
            textDecoration: 'underline',
        },
    },
    externalLinkBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '6px 12px',
        backgroundColor: '#eff6ff',
        border: '1px solid #bfdbfe',
        borderRadius: '6px',
        fontSize: '12px',
        color: '#1e40af',
        fontWeight: 500,
        marginTop: '6px',
        textDecoration: 'none',
        transition: 'all 0.2s',
        maxWidth: '100%',
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: '#dbeafe',
            borderColor: '#93c5fd',
        },
    },
});

const useViewerStyles = makeStyles({
    panel: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#f9fafb',
    },
    header: {
        padding: '16px 24px',
        backgroundColor: 'white',
        borderBottom: '1px solid #e5e7eb',
    },
    title: {
        fontSize: '15px',
        fontWeight: 600,
        color: '#1a1a1a',
    },
    actionButton: {
        padding: '8px 16px',
        backgroundColor: '#14b8a6',
        color: 'white',
        borderRadius: '8px',
        fontSize: '13px',
        fontWeight: 500,
        textTransform: 'none',
        '&:hover': {
            backgroundColor: '#0d9488',
        },
    },
    container: {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        overflow: 'auto',
    },
    frame: {
        width: '100%',
        height: '100%',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
    },
    placeholder: {
        textAlign: 'center',
        color: '#9ca3af',
    },
    placeholderIcon: {
        fontSize: '48px',
        marginBottom: '16px',
    },
    placeholderTitle: {
        fontSize: '16px',
        fontWeight: 500,
        color: '#6b7280',
        marginBottom: '8px',
    },
    placeholderText: {
        fontSize: '14px',
        color: '#9ca3af',
    },
    iframe: {
        border: 'none',
        borderRadius: '8px',
        width: '100%',
        height: '100%',
    },
    externalLinkButton: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        padding: '10px 20px',
        backgroundColor: '#14b8a6',
        color: 'white',
        textDecoration: 'none',
        borderRadius: '8px',
        fontWeight: 500,
        fontSize: '14px',
        transition: 'all 0.2s',
        '&:hover': {
            backgroundColor: '#0d9488',
            textDecoration: 'none !important',
        },
    },
    fileIcon: {
        width: 50,
        height: 50,
        objectFit: 'contain',
    },
    docSendIcon: {
        width: 58,
        height: 58,
        objectFit: 'contain',
    },
});

const mimeTypes: Record<string, string> = {
    // Word Documents
    'doc': 'application/msword',
    'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'docm': 'application/vnd.ms-word.document.macroEnabled.12',
    'txt': 'text/plain',
    'rtf': 'application/rtf',
    'odt': 'application/vnd.oasis.opendocument.text',
    // Excel Spreadsheets
    'xls': 'application/vnd.ms-excel',
    'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'xlsm': 'application/vnd.ms-excel.sheet.macroEnabled.12',
    'xltx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.template',
    'csv': 'text/csv',
    'ods': 'application/vnd.oasis.opendocument.spreadsheet',
    // PowerPoint Presentations
    'ppt': 'application/vnd.ms-powerpoint',
    'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'pptm': 'application/vnd.ms-powerpoint.presentation.macroEnabled.12',
    // Archives
    'zip': 'application/zip',
    'rar': 'application/vnd.rar',
    '7z': 'application/x-7z-compressed',
    'tar': 'application/x-tar',
    'gz': 'application/gzip',
};

// Atoms
const PDFViewer: React.FC<{ pdfBytes?: Uint8Array }> = memo(({ pdfBytes }) => {
    const scrollModePluginInstance = scrollModePlugin();
    const viewerPlugins = [scrollModePluginInstance];

    return (<>{!!pdfBytes ? (
        <Viewer fileUrl={pdfBytes}
            defaultScale={SpecialZoomLevel.PageWidth}
            plugins={viewerPlugins as any[]}
            scrollMode={ScrollMode.Vertical}
            renderError={() => (
                <Stack alignItems="center" justifyContent="center" height="100%">
                    <Typography sx={{
                        backgroundColor: '#e53e3e',
                        borderRadius: '0.25rem',
                        color: '#fff',
                        padding: '0.5rem',
                    }}> {'Error loading the document'} </Typography>
                </Stack>
            )}
        />
    ) : (
        <Stack direction="column" alignItems="center" justifyContent="center" width="100%" height="100%">
            <CircularProgress size="60px" />
        </Stack>
    )}</>);
});

// Molecules
export const DateHeaderMolecule: React.FC<{ docsInGroup: Array<EmailBody & { attached: Attachment[], linked: Attachment[] }> }> = ({ docsInGroup }) => {
    const classes = useTimelineStyles();

    const date = useMemo(() => {
        const sortedFiles = docsInGroup.sort((a, b) => moment(a.createdAt).valueOf() - moment(b.createdAt).valueOf());
        return moment(sortedFiles?.[0]?.createdAt);
    }, [docsInGroup]);

    const attachmentsCount = useMemo(() => {
        return docsInGroup.reduce((sum, email) =>
            sum + email.attached.length + email.linked.length
            , 0);
    }, [docsInGroup]);

    return (
        <Stack direction="row" className={classes.dateHeader}>
            <Stack direction="row" alignItems="baseline" spacing={1}>
                <Typography className={classes.dateText}>{date.format('MMM D, YYYY')}</Typography>
                <Typography className={classes.dateRelative}> • {moment(date).fromNow()}</Typography>
            </Stack>
            <Typography className={classes.dateBadge}>
                {attachmentsCount} file{attachmentsCount > 1 ? 's' : ''}
            </Typography>
        </Stack>
    );
};

export const TimelineItemMolecule: React.FC<{
    file: Attachment;
    isSelected: boolean;
    onClick: () => void;
}> = ({ file, isSelected, onClick }) => {
    const classes = useTimelineStyles();

    return (
        <Box className={classes.timelineItem} onClick={onClick}>
            <Box className={`${classes.timelineContent} ${isSelected ? classes.timelineContentSelected : ''}`}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ marginBottom: '6px' }}>
                    <FileIconAtom fileName={file.name} isLink={file.isLink} linkType={file.linkType} />
                    <Typography className={classes.fileName}>{file.name}</Typography>
                </Stack>
                {!file.isLink && (<>
                    <Box sx={{ pl: '12px', transform: 'scale(1.1)' }}>
                        <FolderChip label="ROOT  ＞  Folder  ＞  subfolder" />
                    </Box>
                </>)}
                {!file.isLink && file.linkUrl && (<>
                    <Box className={classes.fileSource}>
                        <LinkIcon sx={{ fontSize: '12px' }} />
                        <a className={classes.fileSourceLink}
                            href={file.filePath || ''}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}>
                            {file.filePath || ''}
                        </a>
                    </Box>
                </>)}
                {file.isLink && (
                    <a className={classes.externalLinkBadge}
                        href={file.linkUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}>
                        <OpenInNewIcon sx={{ fontSize: '14px' }} />
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {file.linkUrl}
                        </span>
                    </a>
                )}
            </Box>
        </Box>
    );
};

export const TimelineSidebarOrganism: React.FC<{
    dashboardEmailsWithDocs: Array<EmailBody & { attached: Attachment[], linked: Attachment[] }>;
    selectedAttachment: Attachment | null;
    onSelectFile: (file: Attachment) => void;
}> = ({ dashboardEmailsWithDocs, selectedAttachment, onSelectFile }) => {
    const classes = useTimelineStyles();

    const emailBodyByDate = useMemo(() => {
        const grouped: Record<string, Array<EmailBody & { attached: Attachment[], linked: Attachment[] }>> = {};
        dashboardEmailsWithDocs.forEach(emailBody => {
            const raw = emailBody.createdAt;
            const dateKey = raw ? moment(raw).format('MMM D, YYYY') : 'Unknown';
            if (!grouped[dateKey]) grouped[dateKey] = [];
            grouped[dateKey].push(emailBody);
        });
        return grouped;
    }, [dashboardEmailsWithDocs]);

    const sortedDates = useMemo(() => {
        const dateFMT = 'MMM D, YYYY';
        return Object.keys(emailBodyByDate).sort((a, b) => {
            const ma = moment(a, dateFMT, true).isValid() ? moment(a, dateFMT) : moment(a);
            const mb = moment(b, dateFMT, true).isValid() ? moment(b, dateFMT) : moment(b);
            return mb.valueOf() - ma.valueOf();
        });
    }, [emailBodyByDate]);

    const totalDocs = useMemo(() =>
        dashboardEmailsWithDocs.reduce((sum, email) =>
            sum + email.attached.length + email.linked.length
            , 0)
        , [dashboardEmailsWithDocs]);

    return (
        <Stack className={classes.sidebar}>
            <Box className={classes.header}>
                <Typography className={classes.headerTitle}>File Timeline</Typography>
                <Typography className={classes.count}>
                    {totalDocs} file{totalDocs !== 1 ? 's' : ''}
                </Typography>
            </Box>
            <Box className={classes.list}>
                {sortedDates.map((date, idx) => {
                    const docsInGroup = emailBodyByDate[date] as unknown as Array<EmailBody & { attached: Attachment[], linked: Attachment[] }>;

                    return (
                        <Box key={idx} className={classes.dateGroup}>
                            <DateHeaderMolecule docsInGroup={docsInGroup} />
                            {docsInGroup.map(({ attached, linked }, i) => (
                                <Fragment key={'documents-modal-477-' + i}>
                                    {attached.map(file =>
                                        <TimelineItemMolecule
                                            key={file.id}
                                            file={file}
                                            isSelected={selectedAttachment?.id === file.id}
                                            onClick={() => onSelectFile(file)}
                                        />)}
                                    {linked.map(file =>
                                        <TimelineItemMolecule
                                            key={file.id}
                                            file={file}
                                            isSelected={selectedAttachment?.id === file.id}
                                            onClick={() => onSelectFile(file)}
                                        />)}
                                </Fragment>
                            ))}
                        </Box>
                    );
                })}
            </Box>
        </Stack>
    );
};

export const FileViewerOrganism: React.FC<{
    selectedAttachment: Attachment | null;
    loadFromCacheBucket?: boolean;
}> = ({ selectedAttachment, loadFromCacheBucket }) => {
    const classes = useViewerStyles();
    const [pdfBytes, setPdfBytes] = useState<Uint8Array | undefined>(undefined);

    const icon = useMemo(() => {
        // Handle Google, Office 365, and DocSend link types
        if (selectedAttachment?.isLink && selectedAttachment?.linkType) {
            switch (selectedAttachment.linkType) {
                case 'google-docs':
                    return <img src={GoogleDocsIcon} className={classes.fileIcon} alt="google-docs-icon" />;
                case 'google-sheets':
                    return <img src={GoogleSheetsIcon} className={classes.fileIcon} alt="google-sheets-icon" />;
                case 'google-slides':
                    return <img src={GoogleSlidesIcon} className={classes.fileIcon} alt="google-slides-icon" />;
                case 'google-forms':
                    return <InsertDriveFile sx={{ fontSize: '48px', color: '#673ab7' }} />;
                case 'google-drive':
                    return <InsertDriveFile sx={{ fontSize: '48px', color: '#4285f4' }} />;
                case 'office365-word':
                    return <img src={WordIcon} className={classes.fileIcon} alt="word-online-icon" />;
                case 'office365-excel':
                    return <img src={XlsIcon} className={classes.fileIcon} alt="excel-online-icon" />;
                case 'office365-powerpoint':
                    return <img src={PptIcon} className={classes.fileIcon} alt="powerpoint-online-icon" />;
                case 'sharepoint':
                    return <InsertDriveFile sx={{ fontSize: '48px', color: '#0078d4' }} />;
                case 'docsend':
                    return <img src={DocSendIcon} className={classes.docSendIcon} alt="docsend-icon" />;
                default:
                    return <LinkIcon style={{ width: 48, height: 48 }} />;
            }
        }

        // Generic link without specific type
        if (selectedAttachment?.isLink)
            return <LinkIcon style={{ width: 48, height: 48 }} />;

        switch (selectedAttachment?.fileType?.toLowerCase()) {
            // PDF (Red)
            case 'pdf':
                return <PictureAsPdf fontSize="small" sx={{ color: '#EA4335' }} />;
            // Word Documents (Blue)
            case 'doc':
            case 'docx':
            case 'docm':
            case 'txt':
            case 'rtf':
            case 'odt':
                return <img src={WordIcon} className={classes.fileIcon} alt="word-icon" />;
            // Excel Spreadsheets (Green)
            case 'xls':
            case 'xlsx':
            case 'xlsm':
            case 'xltx':
            case 'csv':
            case 'ods':
                return <img src={XlsIcon} className={classes.fileIcon} alt="excel-icon" />;
            // PowerPoint Presentations (Orange)
            case 'ppt':
            case 'pptx':
            case 'pptm':
                return <img src={PptIcon} className={classes.fileIcon} alt="powerpoint-icon" />;
            // Archives (Grey)
            case 'zip':
            case 'rar':
            case '7z':
            case 'tar':
            case 'gz':
                return <img src={ZipIcon} className={classes.fileIcon} alt="archive-icon" />;
            // Default
            default:
                return <InsertDriveFile fontSize="small" />;
        }
        // eslint-disable-next-line
    }, [selectedAttachment]);


    const downloadURL = useCallback((data: any, fileName: any) => {
        const a: any = document.createElement('a');
        a.href = data;
        a.download = fileName;
        document.body.appendChild(a);
        a.style = 'display: none';
        a.click();
        a.remove();
    }, []);

    const downloadBlob = useCallback((data: any, fileName: string, mimeType: string) => {
        const blob = new Blob([data], { type: mimeType });
        const url = window.URL.createObjectURL(blob);

        downloadURL(url, fileName);
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    }, [downloadURL]);

    const handleDownload = useCallback((e) => {
        e.stopPropagation();
        e.preventDefault();
        if (!!selectedAttachment?.key) {
            s3Download({ key: selectedAttachment.key }).then((payload) => {
                downloadBlob(payload, selectedAttachment.name,
                    mimeTypes[selectedAttachment.name.toLowerCase().split('.').pop() || ''] || 'application/octet-stream');
            });
        }
        // eslint-disable-next-line
    }, [selectedAttachment]);

    useEffect(() => {
        if (!selectedAttachment?.key) return;
        let isCancelled = false;

        const assign = (data?: ArrayBuffer | Uint8Array) => {
            if (!data || isCancelled) return;
            setPdfBytes(new Uint8Array(
                data instanceof Uint8Array ? data : data
            ));
        };

        Promise.resolve(loadFromCacheBucket
            ? s3DownloadExternalBucket({ key: selectedAttachment.key, bucket: awsConfig.aws_cache_bucket })
            : s3Download({ key: selectedAttachment.key })
        ).then(assign)
            .catch(() => {
                if (isCancelled) return;
                fetch(selectedAttachment.key)
                    .then(res => res.arrayBuffer())
                    .then(assign);
            });

        return () => {
            isCancelled = true;
            setPdfBytes(undefined);
        };
    }, [loadFromCacheBucket, selectedAttachment]);

    const renderContent = useMemo(() => {
        if (!selectedAttachment) {
            return (
                <Box className={classes.placeholder}>
                    <Box className={classes.placeholderIcon}>📄</Box>
                    <Typography className={classes.placeholderTitle}>
                        {'No file selected'}
                    </Typography>
                    <Typography className={classes.placeholderText}>
                        {'Click on a file from the timeline to preview'}
                    </Typography>
                </Box>
            );
        } else if (selectedAttachment.isLink) {
            return (
                <Box className={classes.placeholder}>
                    <Box className={classes.placeholderIcon}>{icon}</Box>
                    <Typography className={classes.placeholderTitle}>
                        {'External Document Link'}
                    </Typography>
                    <Typography className={classes.placeholderText} sx={{ marginBottom: '16px' }}>
                        {selectedAttachment.linkType
                            ? `This document is hosted on ${getLinkTypeName(selectedAttachment.linkType)}`
                            : 'This document is hosted externally'}
                    </Typography>
                    <a className={classes.externalLinkButton}
                        href={selectedAttachment.linkUrl}
                        target="_blank"
                        rel="noopener noreferrer">
                        <OpenInNewIcon sx={{ fontSize: '16px' }} />
                        {'Open'}
                    </a>
                </Box>
            );
        } else if (selectedAttachment.fileType === 'pdf') {
            return (
                <Box className={classes.iframe}>
                    <PDFViewer pdfBytes={pdfBytes} />
                </Box>
            );
        } else if (
            ['pdf', 'doc', 'docx', 'docm', 'txt', 'rtf', 'odt', 'xls', 'xlsx', 'xlsm', 'xltx', 'csv', 'ods', 'ppt', 'pptx', 'pptm', 'zip', 'rar', '7z', 'tar', 'gz']
                .includes(selectedAttachment.fileType || '')) {
            return (
                <Box className={classes.placeholder}>
                    <Box className={classes.placeholderIcon}>{icon}</Box>
                    <Typography className={classes.placeholderTitle}>
                        {'External Document Link'}
                    </Typography>
                    <Typography className={classes.placeholderText} sx={{ marginBottom: '16px' }}>
                        {`This document is a${['a', 'e', 'f', 'h', 'i', 'l', 'm', 'n', 'r', 's', 'o', 'u', 'x']
                            .includes(selectedAttachment.fileType?.toLowerCase()?.charAt(0) || '') ? 'n' : ''}
                                ${selectedAttachment.fileType?.toUpperCase()} file which you can download by clicking the button below.`}
                    </Typography>
                    <IconButton className={classes.actionButton}
                        onClick={handleDownload} disabled={!selectedAttachment?.filePath}>
                        <DownloadIcon sx={{ fontSize: '16px', marginRight: '6px' }} />
                        {'Download'}
                    </IconButton>
                </Box>
            );
        }

        return (
            <Box className={classes.placeholder}>
                <Box className={classes.placeholderIcon}>
                    <FileIconAtom fileName={selectedAttachment.name} isLink={selectedAttachment.isLink} linkType={selectedAttachment.linkType} />
                </Box>
                <Typography className={classes.placeholderTitle}>
                    {'Preview not available'}
                </Typography>
                <Typography className={classes.placeholderText}>
                    {'This file type cannot be previewed in the browser'}
                </Typography>
            </Box>
        );
        // eslint-disable-next-line
    }, [selectedAttachment, pdfBytes]);

    return (
        <Stack className={classes.panel}>
            <Stack direction="row" className={classes.header}
                alignItems="center" justifyContent="space-between">
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    {selectedAttachment && <FileIconAtom fileName={selectedAttachment.name} isLink={selectedAttachment.isLink} linkType={selectedAttachment.linkType} />}
                    <Typography className={classes.title}>
                        {selectedAttachment?.name || 'Select a file to preview'}
                    </Typography>
                </Stack>
                {selectedAttachment?.fileType === 'pdf' && (
                    <IconButton className={classes.actionButton}
                        onClick={handleDownload} disabled={!selectedAttachment?.filePath}>
                        <DownloadIcon sx={{ fontSize: '16px', marginRight: '6px' }} />
                        {'Download'}
                    </IconButton>
                )}
            </Stack>
            <Box className={classes.container}>
                <Box className={classes.frame}>
                    {renderContent}
                </Box>
            </Box>
        </Stack>
    );
};

export type DashboardEmailWithDocs = EmailBody & { attached: Attachment[], linked: Attachment[] };

const DocumentsModal: React.FC<{
    open: boolean,
    dashboard: Dashboard,
    dashboardEmailsWithDocs: DashboardEmailWithDocs[],
    onClose?: () => void,
}> = ({ open, dashboard, dashboardEmailsWithDocs, onClose }) => {
    const modalClasses = useModalStyles();
    const headerClasses = useHeaderStyles();
    const [selectedAttachment, setSelectedAttachment] = useState<Attachment | null>(null);

    const handleSelectFile = useCallback((file: Attachment) => {
        setSelectedAttachment(file);
    }, []);

    return (
        <Dialog className={modalClasses.dialog}
            open={open} onClose={onClose} maxWidth={false}>
            <Stack className={headerClasses.root} direction="row" alignItems="center" justifyContent="space-between">
                <Typography className={headerClasses.title}>
                    {dashboard.title || 'Documents'}
                </Typography>
                <IconButton onClick={onClose} className={headerClasses.closeButton}>
                    <CloseIcon />
                </IconButton>
            </Stack>
            <Stack direction="row" sx={{ flex: 1, overflow: 'hidden' }}>
                {dashboardEmailsWithDocs.length > 0 && (<>
                    <TimelineSidebarOrganism
                        dashboardEmailsWithDocs={dashboardEmailsWithDocs}
                        selectedAttachment={selectedAttachment}
                        onSelectFile={handleSelectFile} />
                    <FileViewerOrganism selectedAttachment={selectedAttachment} />
                </>)}
            </Stack>
        </Dialog>
    );
};

export default DocumentsModal;